import React, { useState } from 'react';
import { Box, Paper, Stack, Typography, Collapse } from '@mui/material';
import { AttachFile, ExpandMore, ExpandLess } from '@mui/icons-material';
import { DocumentsGrid, EntityDocumentView } from '../../../../../common/components/DocumentUploads';
import { useListAssetDocumentsQuery, useDownloadAssetDocumentMutation } from '../../../../../../services/AssetService/assetApi';

interface AssetDocumentsPanelProps {
  assetId: string;
}

const getDocumentCount = (documents: EntityDocumentView[] | undefined): number =>
  (documents || []).filter(doc => !doc.isDeleted).length;

const getHeaderLabel = (isLoading: boolean, isError: boolean, documentCount: number): string => {
  if (isLoading) return 'Loading documents…';
  if (isError) return 'Unable to load documents';
  if (documentCount === 0) return 'No documents attached to this asset';
  return `Asset Documents (${documentCount})`;
};

interface PanelHeaderProps {
  label: string;
  canExpand: boolean;
  expanded: boolean;
  onToggle: () => void;
}

const PanelHeader: React.FC<PanelHeaderProps> = ({ label, canExpand, expanded, onToggle }) => (
  <Box
    sx={{ px: 2, py: 1.25, backgroundColor: '#f8fafc', cursor: canExpand ? 'pointer' : 'default' }}
    onClick={canExpand ? onToggle : undefined}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Stack direction="row" spacing={1} alignItems="center">
        <AttachFile sx={{ fontSize: 18, color: '#64748b' }} />
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
          {label}
        </Typography>
      </Stack>
      {canExpand && (expanded ? <ExpandLess sx={{ color: '#64748b' }} /> : <ExpandMore sx={{ color: '#64748b' }} />)}
    </Stack>
  </Box>
);

/**
 * AssetDocumentsPanel - read-only, collapsible view of the selected asset's documents on the
 * ticket's Asset Information section. No upload or delete affordance (view/download only).
 */
export const AssetDocumentsPanel: React.FC<AssetDocumentsPanelProps> = ({ assetId }) => {
  const [expanded, setExpanded] = useState(false);
  const { data: documents, isLoading, isError, error } = useListAssetDocumentsQuery(assetId, { skip: !assetId });
  const [downloadAssetDocument] = useDownloadAssetDocumentMutation();

  const documentCount = getDocumentCount(documents);
  const canExpand = !isLoading && !isError && documentCount > 0;
  const headerLabel = getHeaderLabel(isLoading, isError, documentCount);

  return (
    <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, overflow: 'hidden' }}>
      <PanelHeader label={headerLabel} canExpand={canExpand} expanded={expanded} onToggle={() => setExpanded(!expanded)} />

      {canExpand && (
        <Collapse in={expanded} unmountOnExit>
          <Box sx={{ p: 1.5 }}>
            <DocumentsGrid
              entityId={assetId}
              documents={documents}
              isLoading={isLoading}
              isError={isError}
              error={error}
              currentUserName=""
              onDownload={(documentId) => downloadAssetDocument({ assetId, documentId }).unwrap()}
              readOnly
            />
          </Box>
        </Collapse>
      )}
    </Paper>
  );
};

export default AssetDocumentsPanel;
